import React, { useEffect, useState } from 'react';
import { DataGridPro, useGridApiRef, GridFooterContainer } from '@mui/x-data-grid-pro';
import ClipLoader from 'react-spinners/ClipLoader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FilterList } from '@mui/icons-material';
import UnfoldlessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import {
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import Drawer from '@mui/material/Drawer';
import Backdrop from '@mui/material/Backdrop';
import Autocomplete from '@mui/material/Autocomplete';
import {
  TextField, Typography, IconButton, Box,
} from '@mui/material';
import { useFetch } from './api';
import ApiLogDetailDrawer from './ApiLogDetailDrawer';

// date filter button component
function DateFilterButton({
  value, label, width = 'w-20', dateFilter, setDateFilter, setStartDate, setEndDate, setDateRange,
}) {
  return (
    <button
      type="button"
      className={`${width} h-8 px-3 rounded-lg text-sm font-medium transition-all duration-150 ${
        dateFilter === value
          ? 'bg-surface-900 text-surface-0 shadow-xs'
          : 'bg-surface-0 text-surface-600 border border-surface-200 hover:bg-surface-50 hover:border-surface-300'
      }`}
      onClick={() => {
        setDateFilter(value);
        const durations = {
          '14d': [14, 'days'], '7d': [7, 'days'], '1d': [1, 'days'], '1h': [1, 'hour'],
        };
        const [num, unit] = durations[value];
        setStartDate(dayjs().utc().subtract(num, unit));
        setEndDate(dayjs().utc());
        setDateRange([null, null]);
      }}
    >
      {label}
    </button>
  );
}

dayjs.extend(utc);

function ApiLogPage(props) {
  const trackstar = useFetch();
  const [logRows, setLogRows] = useState([]);
  const [logsByStatus, setLogsByStatus] = useState([]);
  const [statusCodes, setStatusCodes] = useState([]);
  const [logsByEndpoint, setLogsByEndpoint] = useState([]);
  const [loadingLogRows, setLoadingLogRows] = useState(false);
  // set on a page click so the footer shows the new range before the rows land
  const [pendingEndRow, setPendingEndRow] = useState(null);
  const [loadingLogsByStatus, setLoadingLogsByStatus] = useState(false);
  const [loadingLogsByEndpoint, setLoadingLogsByEndpoint] = useState(false);
  const [totalAPICalls, setTotalAPICalls] = useState(0);
  const [loadingTotalAPICalls, setLoadingTotalAPICalls] = useState(false);
  const defaultUrlParams = new URLSearchParams(window.location.search);
  const startDateParam = defaultUrlParams.get('start_date');
  const endDateParam = defaultUrlParams.get('end_date');
  const pageTokenParam = defaultUrlParams.get('page_token');
  const pageParam = defaultUrlParams.get('page');
  const startRowParam = defaultUrlParams.get('start_row');
  // a link can carry a page token with a missing or junk start row, and there's no
  // way to work the real one out, so we show a row count instead of a range
  let initialStartRow = 1;
  if (pageTokenParam) {
    const linkedStartRow = parseInt(startRowParam, 10);
    initialStartRow = null;
    if (Number.isSafeInteger(linkedStartRow) && linkedStartRow > 0) {
      initialStartRow = linkedStartRow;
    }
  }
  const [paginationState, setPaginationState] = useState({
    pageSize: 100,
    currentToken: pageTokenParam || null,
    previousTokens: [],
    nextToken: null,
    startRow: initialStartRow,
    previousStartRows: [],
  });
  const [currentPage, setCurrentPage] = useState(pageParam ? parseInt(pageParam, 10) : 1);
  const [sortModel, setSortModel] = useState({
    field: 'request_time',
    sort: 'desc',
  });
  const [startDate, setStartDate] = useState(
    startDateParam ? dayjs.unix(startDateParam) : dayjs().subtract(1, 'days'),
  );
  const [endDate, setEndDate] = useState(
    endDateParam ? dayjs.unix(endDateParam) : dayjs(),
  );
  const dateFilterParam = defaultUrlParams.get('date_filter');
  const [dateFilter, setDateFilter] = useState(dateFilterParam || '1d');
  const connectionParam = defaultUrlParams.get('connection');
  const [connectionFilter, setConnectionFilter] = useState(connectionParam || null);
  const integrationParam = defaultUrlParams.get('integration');
  const [integrationFilter, setIntegrationFilter] = useState(integrationParam || null);
  const statusCodeParam = defaultUrlParams.get('status_code');
  const [statusCodeFilter, setStatusCodeFilter] = useState(statusCodeParam || null);
  const requestMethodParam = defaultUrlParams.get('request_method');
  const [requestMethodFilter, setRequestMethodFilter] = useState(requestMethodParam || null);
  const requestPathParam = defaultUrlParams.get('request_path');
  const [requestPathFilter, setRequestPathFilter] = useState(requestPathParam || null);
  const errorsParam = defaultUrlParams.get('errors');
  const [errorsFilter, setErrorsFilter] = useState(errorsParam === 'true');
  const [showApiLogDetail, setShowApiLogDetail] = useState(false);
  const [apiLogDetail, setApiLogDetail] = useState(null);
  const [hideChartWidgets, setHideChartWidgets] = useState(true);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [requestPaths, setRequestPaths] = useState([]);
  const [requestPathToTitleMap, setRequestPathToTitleMap] = useState({});
  const [dateRange, setDateRange] = useState([null, null]);
  const [hoveredStatus, setHoveredStatus] = useState(null);
  const [statusFilterPopover, setStatusFilterPopover] = useState({
    open: false, status: null, x: 0, y: 0,
  });
  const [endpointFilterPopover, setEndpointFilterPopover] = useState({
    open: false, path: null, method: null, x: 0, y: 0,
  });
  const [showAllStatuses, setShowAllStatuses] = useState(false);

  const gridApiRef = useGridApiRef();
  const isInitialMount = React.useRef(true);
  // tracks whether the grid is scrolled down, so we can show the live-paused hint
  const [isScrolled, setIsScrolled] = useState(false);

  const {
    connectionData,
    loadingConnectionData,
    integrationLists,
    integrationListsLoading,
    requestPathToReadableTitleLookup,
    loadingOpenApiSpec,
  } = props;

  // softer status colors for charts
  const statusShadesMap = {
    200: '#6EE7B7', // emerald-300 - soft green
    400: '#FECACA', // red-200
    401: '#FCA5A5', // red-300
    403: '#F87171', // red-400
    404: '#EF4444', // red-500
    405: '#DC2626', // red-600
    409: '#B91C1C', // red-700
    422: '#991B1B', // red-800
    429: '#FCD34D', // amber-300 - soft amber
    500: '#FCA5A5',
    501: '#FCA5A5',
    502: '#FCA5A5',
    503: '#FCA5A5',
    504: '#FCA5A5',
  };

  // chart styling constants
  const chartAxisStyle = {
    fontSize: '11px',
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fill: '#78716C',
  };

  const chartGridStyle = {
    stroke: '#E7E5E4',
    strokeDasharray: '4 4',
  };

  const httpStatusCodes = [
    '200', // OK
    '400', // Bad Request
    '401', // Unauthorized
    '403', // Forbidden
    '404', // Not Found
    '405', // Method Not Allowed
    '409', // Conflict
    '422', // Unprocessable Entity
    '429', // Too Many Requests
    '500', // Internal Server Error
    '501', // Not Implemented
    '502', // Bad Gateway
    '503', // Service Unavailable
    '504', // Gateway Timeout
  ];

  const httpMethods = [
    'GET',
    'POST',
    'PUT',
  ];

  const integrationValues = [];
  if (integrationLists) {
    Object.values(integrationLists).forEach((integrationList, index) => {
      const integrationNames = Object.keys(integrationList);
      const integrationType = Object.keys(integrationLists)[index];
      // push in {"label": displayName, "value": value}
      integrationNames.forEach((integrationName) => {
        let displayName = integrationList[integrationName].display_name;
        if (integrationType !== 'wms') {
          displayName = `${displayName} (${integrationType})`;
        }
        integrationValues.push({
          label: displayName,
          value: integrationName,
        });
      });
    });
    integrationValues.sort((a, b) => a.label.localeCompare(b.label));
  }

  // count active filters
  const activeFilterCount = [
    connectionFilter,
    integrationFilter,
    statusCodeFilter,
    requestMethodFilter,
    requestPathFilter,
    errorsFilter,
  ].filter(Boolean).length;

  // clear all filters
  const clearAllFilters = () => {
    setConnectionFilter(null);
    setIntegrationFilter(null);
    setStatusCodeFilter(null);
    setRequestMethodFilter(null);
    setRequestPathFilter(null);
    setErrorsFilter(false);
  };

  const getTimeBreakdown = (end, start) => {
    let timeBreakdown;
    let tickFormatter;
    const endUnix = end.unix();
    const startUnix = start.unix();
    const hour = 60 * 60; // seconds in an hour
    const day = 24 * hour; // seconds in a day
    const month = 30 * day; // seconds in a month
    if (endUnix - startUnix >= 6 * month) {
      timeBreakdown = 'month';
      tickFormatter = (value) => dayjs.utc(value).format('MMM YYYY');
    } else if (endUnix - startUnix >= 5 * day) {
      timeBreakdown = 'day';
      tickFormatter = (value) => dayjs.utc(value).format('MMMM D');
    } else if (endUnix - startUnix >= 12 * hour) {
      timeBreakdown = 'hour';
      // Convert to 12 hour time in local time
      tickFormatter = (value) => {
        const localTime = dayjs.utc(value).local();
        return localTime.format('h A');
      };
    } else if (endUnix - startUnix < day) {
      timeBreakdown = '5minute';
      tickFormatter = (value) => {
        const localTime = dayjs.utc(value).local();
        return localTime.format('h:mm A');
      };
    } else { // Default to hour?
      timeBreakdown = 'hour';
      tickFormatter = (value) => {
        const localTime = dayjs.utc(value).local();
        return localTime.format('h A');
      };
    }
    return { timeBreakdown, tickFormatter };
  };

  const getBarChartTooltip = ({ payload: entries, label }) => {
    if (entries && entries.length) {
      const formatter = getTimeBreakdown(endDate, startDate).tickFormatter;
      const formattedLabel = formatter(label);
      const entryFormatter = (entry) => `${entry.dataKey}: ${entry.payload[entry.dataKey]}`;
      return (
        <div className="chart-tooltip">
          <p className="text-xs font-medium text-surface-500 mb-2">{formattedLabel}</p>
          {entries.map((entry) => (
            <div key={entry.dataKey} className="flex items-center gap-2 py-0.5">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm font-medium text-surface-700">
                {entryFormatter(entry)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleStatusLegendClick = (status, event) => {
    const rect = event.target.getBoundingClientRect();
    setStatusFilterPopover({
      open: true,
      status,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
  };

  const handleEndpointClick = (path, method, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setEndpointFilterPopover({
      open: true,
      path,
      method,
      x: rect.left + 100,
      y: rect.bottom + 4,
    });
  };

  const renderStatusLegend = (legendProps) => {
    const { payload } = legendProps;

    // calculate total count for each status code from logsByStatus data
    const statusTotals = {};
    logsByStatus.forEach((row) => {
      statusCodes.forEach((code) => {
        if (row[code]) {
          statusTotals[code] = (statusTotals[code] || 0) + row[code];
        }
      });
    });

    // sort payload by count (descending)
    const sortedPayload = [...payload].sort((a, b) => {
      const countA = statusTotals[a.value] || 0;
      const countB = statusTotals[b.value] || 0;
      return countB - countA;
    });

    const maxVisible = 5;
    const hasMore = sortedPayload.length > maxVisible;
    const visiblePayload = showAllStatuses ? sortedPayload : sortedPayload.slice(0, maxVisible);

    return (
      <div className="flex flex-wrap mt-2 items-center">
        {visiblePayload.map((entry) => (
          <button
            key={entry.value}
            type="button"
            className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded transition-all cursor-pointer ${
              hoveredStatus && hoveredStatus !== String(entry.value)
                ? 'opacity-40'
                : 'opacity-100 hover:bg-surface-100'
            }`}
            onMouseEnter={() => setHoveredStatus(String(entry.value))}
            onMouseLeave={() => setHoveredStatus(null)}
            onClick={(e) => handleStatusLegendClick(entry.value, e)}
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-xs text-surface-600">{entry.value}</span>
          </button>
        ))}
        {hasMore && (
          <button
            type="button"
            className="text-xs text-surface-400 hover:text-surface-600 px-1.5 py-0.5"
            onClick={() => setShowAllStatuses(!showAllStatuses)}
          >
            {showAllStatuses ? 'Show less' : `+${sortedPayload.length - maxVisible} more`}
          </button>
        )}
      </div>
    );
  };

  const getBreakdownData = () => {
    setLoadingLogsByStatus(true);
    setLoadingLogsByEndpoint(true);
    setShowAllStatuses(false);
    const startDateUTC = startDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    const endDateUTC = endDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    const { timeBreakdown } = getTimeBreakdown(endDate, startDate);

    let statusBreakdown = `internal/api-logs-breakdown?start_date=${startDateUTC}&end_date=${endDateUTC}&groups=${timeBreakdown},status_code`;
    if (connectionFilter) {
      statusBreakdown += `&connection_id=${connectionFilter}`;
    }
    if (integrationFilter) {
      statusBreakdown += `&integration_name=${integrationFilter}`;
    }
    if (statusCodeFilter) {
      statusBreakdown += `&status_code=${statusCodeFilter}`;
    }
    if (requestMethodFilter) {
      statusBreakdown += `&request_method=${requestMethodFilter}`;
    }
    if (requestPathFilter) {
      statusBreakdown += `&request_path=${requestPathFilter}`;
    }
    if (errorsFilter) {
      statusBreakdown += '&errors=true';
    }
    trackstar(statusBreakdown, 'GET').then((response) => {
      const { data } = response;
      const formatted = {};
      const codes = [];
      for (let i = 0; i < data.length; i += 1) {
        const entry = data[i];
        if (formatted[entry.request_time] == null) {
          formatted[entry.request_time] = {};
        }
        formatted[entry.request_time][entry.status_code] = entry.count;
        if (!codes.includes(entry.status_code)) {
          codes.push(entry.status_code);
        }
      }
      const sortedKeys = Object.keys(formatted).sort((a, b) => dayjs(a).unix() - dayjs(b).unix());
      const sortedData = [];
      for (let i = 0; i < sortedKeys.length; i += 1) {
        const entry = formatted[sortedKeys[i]];
        entry.request_time = sortedKeys[i];
        sortedData.push(entry);
      }
      setLogsByStatus(sortedData);
      setStatusCodes(codes);
      setLoadingLogsByStatus(false);
    });
    let endpointBreakdown = `internal/api-logs-breakdown?start_date=${startDateUTC}&end_date=${endDateUTC}&groups=request_path,request_method`;
    if (connectionFilter) {
      endpointBreakdown += `&connection_id=${connectionFilter}`;
    }
    if (integrationFilter) {
      endpointBreakdown += `&integration_name=${integrationFilter}`;
    }
    if (statusCodeFilter) {
      endpointBreakdown += `&status_code=${statusCodeFilter}`;
    }
    if (requestMethodFilter) {
      endpointBreakdown += `&request_method=${requestMethodFilter}`;
    }
    if (requestPathFilter) {
      endpointBreakdown += `&request_path=${requestPathFilter}`;
    }
    if (errorsFilter) {
      endpointBreakdown += '&errors=true';
    }
    trackstar(endpointBreakdown, 'GET').then((response) => {
      const { data } = response;
      setLogsByEndpoint(data);
      setLoadingLogsByEndpoint(false);
      const paths = data.map((entry) => entry.request_path);
      const uniquePaths = [...new Set(paths)].sort((a, b) => a.localeCompare(b));
      setRequestPaths(uniquePaths);
    });
  };

  const getAPILogRows = (pageToken = null, customSortModel = null) => {
    setLoadingLogRows(true);
    const startDateUTC = startDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    const endDateUTC = endDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    let url = `internal/api-logs?start_date=${startDateUTC}&end_date=${endDateUTC}&limit=${paginationState.pageSize}`;

    if (pageToken) {
      url += `&page_token=${encodeURIComponent(pageToken)}`;
    }

    const currentSortModel = customSortModel || sortModel;
    if (currentSortModel && currentSortModel.field) {
      url += `&sort_by=${currentSortModel.field}&sort_order=${currentSortModel.sort}`;
    }

    if (connectionFilter) {
      url += `&connection_id=${connectionFilter}`;
    }
    if (integrationFilter) {
      url += `&integration_name=${integrationFilter}`;
    }
    if (statusCodeFilter) {
      url += `&status_code=${statusCodeFilter}`;
    }
    if (requestMethodFilter) {
      url += `&request_method=${requestMethodFilter}`;
    }
    if (requestPathFilter) {
      url += `&request_path=${requestPathFilter}`;
    }
    if (errorsFilter) {
      url += '&errors=true';
    }
    trackstar(url, 'GET').then((response) => {
      const { data, next_token: nextToken } = response;
      setLogRows(data);
      setPaginationState((prev) => ({
        ...prev,
        currentToken: pageToken,
        nextToken,
      }));
    }).finally(() => {
      // paging is blocked while this is true, so it has to clear on a failed
      // request too or the arrows stay dead until a reload
      setLoadingLogRows(false);
      setPendingEndRow(null);
    });
  };

  const getAPILogRowsCount = () => {
    setLoadingTotalAPICalls(true);
    const startDateUTC = startDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    const endDateUTC = endDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    let url = `internal/api-logs-count?start_date=${startDateUTC}&end_date=${endDateUTC}`;
    if (connectionFilter) {
      url += `&connection_id=${connectionFilter}`;
    }
    if (integrationFilter) {
      url += `&integration_name=${integrationFilter}`;
    }
    if (statusCodeFilter) {
      url += `&status_code=${statusCodeFilter}`;
    }
    if (requestMethodFilter) {
      url += `&request_method=${requestMethodFilter}`;
    }
    if (requestPathFilter) {
      url += `&request_path=${requestPathFilter}`;
    }
    if (errorsFilter) {
      url += '&errors=true';
    }
    trackstar(url, 'GET').then((response) => {
      const { data } = response;
      setTotalAPICalls(data);
      setLoadingTotalAPICalls(false);
    });
  };

  const updatePageInUrl = (pageToken, page, startRow) => {
    const urlParams = new URLSearchParams(window.location.search);
    if (pageToken) {
      urlParams.set('page_token', pageToken);
      urlParams.set('page', page);
    } else {
      urlParams.delete('page_token');
      urlParams.delete('page');
    }
    if (pageToken && startRow) {
      urlParams.set('start_row', startRow);
    } else {
      urlParams.delete('start_row');
    }
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  };

  // assume a full page, capped at the total. a page cut short by the api's size
  // limit corrects itself once the rows land
  const estimateEndRow = (startRow) => {
    const fullPageEnd = startRow + paginationState.pageSize - 1;
    if (loadingTotalAPICalls || totalAPICalls < startRow) {
      return fullPageEnd;
    }
    return Math.min(fullPageEnd, totalAPICalls);
  };

  const goToNextPage = () => {
    if (paginationState.nextToken) {
      const { nextToken, startRow } = paginationState;
      const newPage = currentPage + 1;
      // a page comes back short when the api cuts it off to keep the response under
      // its size limit, so count the rows we actually got instead of assuming a full page
      const newStartRow = startRow ? startRow + logRows.length : null;
      setPaginationState((prev) => ({
        ...prev,
        previousTokens: [...prev.previousTokens, prev.currentToken],
        previousStartRows: [...prev.previousStartRows, prev.startRow],
        startRow: newStartRow,
      }));
      setCurrentPage(newPage);
      setPendingEndRow(newStartRow ? estimateEndRow(newStartRow) : null);
      updatePageInUrl(nextToken, newPage, newStartRow);
      getAPILogRows(nextToken);
    }
  };

  const goToPreviousPage = () => {
    const previousTokens = [...paginationState.previousTokens];
    const previousToken = previousTokens.pop();
    const previousStartRows = [...paginationState.previousStartRows];
    const previousStartRow = previousStartRows.pop() ?? null;
    const newPage = Math.max(1, currentPage - 1);

    setPaginationState((prev) => ({
      ...prev,
      previousTokens,
      previousStartRows,
      startRow: previousStartRow,
    }));
    setCurrentPage(newPage);
    // the previous page ends right before the current one starts, so this is exact
    const { startRow } = paginationState;
    setPendingEndRow(previousStartRow && startRow ? startRow - 1 : null);
    updatePageInUrl(previousToken, newPage, previousStartRow);
    getAPILogRows(previousToken);
  };

  const goToFirstPage = () => {
    setPaginationState((prev) => ({
      ...prev,
      currentToken: null,
      previousTokens: [],
      previousStartRows: [],
      startRow: 1,
    }));
    setCurrentPage(1);
    setPendingEndRow(estimateEndRow(1));
    updatePageInUrl(null, null, null);
    getAPILogRows(null);
  };

  const resetPagination = () => {
    setPaginationState((prev) => ({
      ...prev,
      currentToken: null,
      previousTokens: [],
      previousStartRows: [],
      nextToken: null,
      startRow: 1,
    }));
    setCurrentPage(1);
    updatePageInUrl(null, null, null);
  };

  // Function to refresh all data
  const refreshAllData = () => {
    getBreakdownData();
    getAPILogRows();
    getAPILogRowsCount();
  };

  // Set up auto-refresh on component mount and clean up on unmount
  useEffect(() => {
    // Don't refresh if date filter is custom
    if (dateFilter === 'custom') {
      return () => {};
    }
    // Set up auto-refresh every 15 seconds
    const intervalId = setInterval(() => {
      // only live-refresh on the first page — paging away pauses it so new logs
      // don't reset the user back to page 1
      if (currentPage !== 1) {
        return;
      }
      // don't refresh while scrolled down, so we don't yank the user to the top
      // mid-read
      const scrollTop = gridApiRef.current?.getScrollPosition?.()?.top ?? 0;
      if (scrollTop > 0) {
        return;
      }
      // Just update the endDate state
      setEndDate(dayjs());
    }, 15000);

    // Clean up interval on component unmount
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [dateFilter, currentPage]);

  // keep the live-paused hint in sync with the grid's scroll position
  useEffect(() => {
    const api = gridApiRef.current;
    if (!api || !api.subscribeEvent) {
      return undefined;
    }
    return api.subscribeEvent('scrollPositionChange', (params) => {
      setIsScrolled((params?.top ?? 0) > 0);
    });
  }, [gridApiRef, logRows.length]);

  useEffect(() => {
    if (isInitialMount.current) {
      // on initial mount, use page token from URL if present
      isInitialMount.current = false;
      getBreakdownData();
      getAPILogRows(pageTokenParam || null);
      getAPILogRowsCount();
    } else {
      // on subsequent changes, reset pagination
      resetPagination();
      refreshAllData();
    }
    const urlParams = new URLSearchParams(window.location.search);
    const currentPath = window.location.pathname;
    urlParams.set('start_date', startDate.unix());
    urlParams.set('end_date', endDate.unix());
    urlParams.set('date_filter', dateFilter);
    if (connectionFilter) {
      urlParams.set('connection', connectionFilter);
    } else {
      urlParams.delete('connection');
    }
    if (integrationFilter) {
      urlParams.set('integration', integrationFilter);
    } else {
      urlParams.delete('integration');
    }
    if (statusCodeFilter) {
      urlParams.set('status_code', statusCodeFilter);
    } else {
      urlParams.delete('status_code');
    }
    if (requestMethodFilter) {
      urlParams.set('request_method', requestMethodFilter);
    } else {
      urlParams.delete('request_method');
    }
    if (requestPathFilter) {
      urlParams.set('request_path', requestPathFilter);
    } else {
      urlParams.delete('request_path');
    }
    if (errorsFilter) {
      urlParams.set('errors', 'true');
    } else {
      urlParams.delete('errors');
    }
    // page_token, page and start_row are managed by updatePageInUrl
    // preserve them if they exist in the current URL
    const existingParams = new URLSearchParams(window.location.search);
    const existingPageToken = existingParams.get('page_token');
    const existingPage = existingParams.get('page');
    const existingStartRow = existingParams.get('start_row');
    if (existingPageToken) {
      urlParams.set('page_token', existingPageToken);
      urlParams.set('page', existingPage);
      if (existingStartRow) {
        urlParams.set('start_row', existingStartRow);
      }
    }
    const newUrl = `${currentPath}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  }, [
    startDate,
    endDate,
    dateFilter,
    connectionFilter,
    integrationFilter,
    statusCodeFilter,
    requestMethodFilter,
    requestPathFilter,
    errorsFilter,
    sortModel,
  ]);

  useEffect(() => {
    getAPILogRowsCount();
    getBreakdownData();
  }, [
    startDate,
    endDate,
    dateFilter,
    connectionFilter,
    integrationFilter,
    statusCodeFilter,
    requestMethodFilter,
    requestPathFilter,
    errorsFilter,
  ]);

  const getDisplayName = (integrationType, value) => {
    if (value == null) {
      return null;
    }
    if (integrationListsLoading) {
      return <ClipLoader size={15} />;
    }
    let integration = null;
    if (integrationType) {
      const integrationList = integrationLists[integrationType];
      integration = integrationList[value];
    } else {
      Object.keys(integrationLists).forEach((potentialType) => {
        if (value in integrationLists[potentialType]) {
          integration = integrationLists[potentialType][value];
        }
      });
    }
    const displayName = integration ? integration.display_name : value;
    return displayName;
  };

  const removeIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('id');
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  };

  const openApiLogDetail = (row) => {
    const logId = row.id;
    setShowApiLogDetail(true);
    setApiLogDetail(row);
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const newSearch = new URLSearchParams(currentSearch);
    newSearch.set('id', logId);
    window.history.pushState({}, '', `${currentPath}?${newSearch.toString()}`);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const logId = urlParams.get('id');
    if (logId && logRows) {
      const log = logRows.find((row) => row.id === logId);
      if (log) {
        openApiLogDetail(log);
      }
    }
    if (loadingOpenApiSpec) {
      return;
    }

    const pathToTitleMap = {};
    logRows.forEach(({ request_path: requestPath, request_method: requestMethod }) => {
      const method = String(requestMethod).toUpperCase();
      const isInstall = requestPath.includes('install');

      // Normalize dynamic segments like <order_id> → <id>,
      // unless it's an integrations path (which uses <path_param>)
      const normalizedPath = requestPath.includes('integrations')
        ? requestPath
        : requestPath.replace(/<[^>]+>/, '<id>');

      // For install paths, keep the raw path as the key
      const keyPath = isInstall ? requestPath : normalizedPath;

      const title = isInstall
        ? 'Install Integration'
        : requestPathToReadableTitleLookup.get(normalizedPath)?.get(method);

      if (!title) return;

      // Ensure we have an object for this path, then store method → title
      if (!pathToTitleMap[keyPath]) {
        pathToTitleMap[keyPath] = {};
      }
      pathToTitleMap[keyPath][method] = title;
    }, {});

    setRequestPathToTitleMap(pathToTitleMap);
  }, [logRows, loadingOpenApiSpec, requestPathToReadableTitleLookup]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!showApiLogDetail) return;

      if (event.key === 'Escape') {
        setShowApiLogDetail(false);
        removeIdFromUrl();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const currentIndex = logRows.findIndex((row) => row.id === apiLogDetail?.id);
        if (currentIndex === -1) return;

        const newIndex = event.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, logRows.length - 1)
          : Math.max(currentIndex - 1, 0);

        if (newIndex !== currentIndex) {
          const newRow = logRows[newIndex];
          setApiLogDetail(newRow);
          // update URL
          const currentPath = window.location.pathname;
          const currentSearch = window.location.search;
          const newSearch = new URLSearchParams(currentSearch);
          newSearch.set('id', newRow.id);
          window.history.pushState({}, '', `${currentPath}?${newSearch.toString()}`);
          // scroll row into view
          gridApiRef.current.scrollToIndexes({ rowIndex: newIndex });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showApiLogDetail, logRows, apiLogDetail, gridApiRef]);

  const colHeaders = [
    {
      field: 'request_time',
      headerName: 'Request Time',
      width: 175,
      filterable: false,
      valueFormatter: (value) => {
        const date = new Date(value);
        return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
      },
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      display: 'flex',
      width: 90,
      renderCell: (params) => {
        if (params.value == null) return <span className="text-xs font-mono text-surface-500">—</span>;
        const label = params.value >= 1000
          ? `${(params.value / 1000).toFixed(1)}s`
          : `${params.value}ms`;
        return <span className="text-xs font-mono text-surface-500">{label}</span>;
      },
    },
    {
      field: 'request_path',
      headerName: 'Request URL',
      display: 'flex',
      flex: 1.5,
      minWidth: 280,
      renderCell: (params) => (
        <div className="flex gap-2 items-center">
          {/* request-GET request-POST request-PUT request-PATCH request-DELETE */}
          <div className={`request-${params.row.request_method} w-14 text-center flex-shrink-0`}>
            {params.row.request_method}
          </div>
          <p className="truncate">{params.row.request_path}</p>
        </div>
      ),
    },
    {
      field: 'status_code',
      headerName: 'Status',
      display: 'flex',
      width: 100,
      renderCell: (params) => (
        <span className={`badge ${params.value === 200 ? 'badge-success' : 'badge-error'}`}>
          {params.value}
        </span>
      ),
    },
    {
      field: 'integration_name',
      headerName: 'Integration',
      display: 'flex',
      width: 200,
      valueFormatter: (value) => {
        if (value == null) {
          return null;
        }
        return value.charAt(0).toUpperCase() + value.slice(1);
      },
      renderCell: (params) => {
        const { value } = params;
        let logo = `https://trackstarlogosbucket.s3.amazonaws.com/${value}.png`;
        if (value == null) {
          return <p>None</p>;
        }
        if (value === 'sandbox') {
          logo = 'ts_logo_2024.png';
        }
        const integrationType = params.row.integration_type;
        return (
          <div className="flex gap-2.5 items-center">
            <div className="w-6 h-6 rounded-md bg-surface-100 flex items-center justify-center border border-surface-200">
              <img src={logo} alt={`${value} logo`} className="h-4 w-4" />
            </div>
            <span className="text-surface-700">{getDisplayName(integrationType, value)}</span>
          </div>
        );
      },
    },
    {
      field: 'connection_id',
      headerName: 'Connection ID',
      flex: 1,
      minWidth: 200,
    },
  ];

  const customFooter = () => {
    const { startRow } = paginationState;
    const totalCount = loadingTotalAPICalls ? '...' : totalAPICalls.toLocaleString();
    let rangeLabel = logRows.length.toLocaleString();
    if (pendingEndRow && startRow) {
      // logRows is still the previous page until the fetch lands, so use the
      // range computed on click. live refreshes keep the rows on screen and skip this
      rangeLabel = `${startRow.toLocaleString()}–${pendingEndRow.toLocaleString()}`;
    } else if (logRows.length === 0) {
      rangeLabel = '0';
    } else if (startRow) {
      const endRow = startRow + logRows.length - 1;
      rangeLabel = `${startRow.toLocaleString()}–${endRow.toLocaleString()}`;
    }

    return (
      <GridFooterContainer>
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 2,
              paddingRight: 2,
              minHeight: '52px',
            }}
          >
            <Box sx={{ flex: '1 1 100%' }} />
            <Typography
              className="MuiTablePagination-displayedRows"
              sx={{ fontSize: '0.875rem', flexShrink: 0 }}
            >
              {rangeLabel}
              {' '}
              of
              {' '}
              {totalCount}
            </Typography>
            <Box className="MuiTablePagination-actions" sx={{ flexShrink: 0, ml: 2.5 }}>
              {currentPage > 1 && paginationState.previousTokens.length === 0 && (
                <IconButton
                  onClick={goToFirstPage}
                  disabled={loadingLogRows}
                  aria-label="Go to first page"
                  title="Go to first page"
                  size="medium"
                >
                  <FirstPageIcon />
                </IconButton>
              )}
              <IconButton
                onClick={goToPreviousPage}
                disabled={loadingLogRows || paginationState.previousTokens.length === 0}
                aria-label="Go to previous page"
                title="Go to previous page"
                size="medium"
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
              <IconButton
                onClick={goToNextPage}
                disabled={loadingLogRows || !paginationState.nextToken}
                aria-label="Go to next page"
                title="Go to next page"
                size="medium"
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </GridFooterContainer>
    );
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-baseline gap-3">
          <h1 className="page-title">API Activity</h1>
          <span className="page-subtitle">Review and audit API calls</span>
        </div>
        <div className="flex items-center gap-6">
          {/* Date filter buttons */}
          <div className="flex gap-1.5">
            <DateFilterButton value="14d" label="14 days" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
            <DateFilterButton value="7d" label="7 days" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
            <DateFilterButton value="1d" label="24 hours" width="w-24" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
            <DateFilterButton value="1h" label="1 hour" width="w-20" dateFilter={dateFilter} setDateFilter={setDateFilter} setStartDate={setStartDate} setEndDate={setEndDate} setDateRange={setDateRange} />
          </div>
          <div className="flex items-center gap-3">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="w-52">
                <DateRangePicker
                  slots={{ field: SingleInputDateRangeField }}
                  name="allowedRange"
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                  sx={{
                    '& .MuiPickersTextField-root, & .MuiPickersOutlinedInput-root': {
                      borderRadius: '10px',
                      height: '32px',
                      fontSize: '0.8125rem',
                      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E7E5E4',
                      '&:hover': {
                        borderColor: '#D6D3D1',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                  disableFuture
                  minDate={dayjs().subtract(12, 'months')}
                  value={dateRange}
                  onChange={(newValue) => {
                    setDateRange(newValue);
                    if (newValue[0] && newValue[1]) {
                      const start = newValue[0].hour(23).minute(59).second(59);
                      const end = newValue[1].hour(23).minute(59).second(59);
                      setStartDate(start);
                      setEndDate(end);
                      setDateFilter('custom');
                    }
                  }}
                />
              </div>
            </LocalizationProvider>
            <button
              type="button"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                showFiltersPanel || activeFilterCount > 0
                  ? 'bg-surface-900 text-white border-surface-900'
                  : 'bg-surface-0 text-surface-600 border-surface-200 hover:bg-surface-50'
              }`}
            >
              <FilterList sx={{ fontSize: '16px' }} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white/20 text-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {dateFilter !== 'custom' && (
              <>
                <div className="w-px h-6 bg-surface-200" />
                {/* fixed width so the header doesn't wiggle between Live/Paused */}
                <div className="w-16">
                  {(currentPage === 1 && !isScrolled) ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-surface-500 whitespace-nowrap">
                      <span className="h-2 w-2 rounded-full bg-success animate-pulse" />
                      Live
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs text-surface-400 whitespace-nowrap"
                      title={currentPage !== 1
                        ? 'Return to page 1 to resume live updates'
                        : 'Scroll up to resume live updates'}
                    >
                      <span className="h-2 w-2 rounded-full bg-surface-300" />
                      Paused
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Filter popover */}
      {showFiltersPanel && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowFiltersPanel(false)}
            onKeyDown={(e) => e.key === 'Escape' && setShowFiltersPanel(false)}
            role="button"
            tabIndex={0}
            aria-label="Close filters"
          />
          <div className="absolute right-10 top-20 z-50 bg-surface-0 rounded-xl border border-surface-200 shadow-xl p-5 w-80">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-surface-800">Filters</span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs text-surface-500 hover:text-surface-700"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <span className="block text-xs font-medium text-surface-500 mb-1.5">Connection</span>
                <Autocomplete
                  options={loadingConnectionData ? [] : connectionData?.map((c) => c.connection_id)}
                  value={connectionFilter}
                  onChange={(_, newValue) => setConnectionFilter(newValue === 'all' ? null : newValue)}
                  renderInput={(params) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      placeholder="All connections"
                      sx={{ '& .MuiOutlinedInput-input': { height: '15px' } }}
                    />
                  )}
                  size="small"
                  fullWidth
                  slotProps={{
                    popper: { style: { width: '300px' }, placement: 'bottom-start' },
                    paper: {
                      sx: {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: '1px solid #E7E5E4',
                        mt: 0.5,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: '8px',
                      height: '36px',
                      fontSize: '0.8125rem',
                      fontFamily: '"Plus Jakarta Sans"',
                      backgroundColor: '#fff',
                    },
                  }}
                />
              </div>
              <div>
                <span className="block text-xs font-medium text-surface-500 mb-1.5">Integration</span>
                <Autocomplete
                  options={integrationListsLoading ? [] : integrationValues}
                  value={integrationFilter}
                  onChange={(_, newValue) => setIntegrationFilter(newValue === 'all' || newValue === null ? null : newValue.value)}
                  renderInput={(params) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      placeholder="All integrations"
                      sx={{ '& .MuiOutlinedInput-input': { height: '15px' } }}
                    />
                  )}
                  size="small"
                  fullWidth
                  slotProps={{
                    popper: { style: { width: '280px' }, placement: 'bottom-start' },
                    paper: {
                      sx: {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: '1px solid #E7E5E4',
                        mt: 0.5,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: '8px',
                      height: '36px',
                      fontSize: '0.8125rem',
                      fontFamily: '"Plus Jakarta Sans"',
                      backgroundColor: '#fff',
                    },
                  }}
                />
              </div>
              <div>
                <span className="block text-xs font-medium text-surface-500 mb-1.5">Path</span>
                <Autocomplete
                  options={requestPaths || []}
                  value={requestPathFilter}
                  onChange={(_, newValue) => setRequestPathFilter(newValue === 'all' || newValue === null ? null : newValue)}
                  renderInput={(params) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      placeholder="All paths"
                      sx={{ '& .MuiOutlinedInput-input': { height: '15px' } }}
                    />
                  )}
                  size="small"
                  fullWidth
                  slotProps={{
                    popper: { style: { width: '280px' }, placement: 'bottom-start' },
                    paper: {
                      sx: {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: '1px solid #E7E5E4',
                        mt: 0.5,
                      },
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: '8px',
                      height: '36px',
                      fontSize: '0.8125rem',
                      fontFamily: '"Plus Jakarta Sans"',
                      backgroundColor: '#fff',
                    },
                  }}
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <span className="block text-xs font-medium text-surface-500 mb-1.5">Method</span>
                  <Autocomplete
                    options={httpMethods}
                    value={requestMethodFilter}
                    onChange={(_, newValue) => setRequestMethodFilter(newValue === 'all' || newValue === null ? null : newValue)}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        placeholder="Any"
                        sx={{ '& .MuiOutlinedInput-input': { height: '15px' } }}
                      />
                    )}
                    size="small"
                    fullWidth
                    slotProps={{
                      popper: { style: { width: '140px' }, placement: 'bottom-start' },
                      paper: {
                        sx: {
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          border: '1px solid #E7E5E4',
                          mt: 0.5,
                        },
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '8px',
                        height: '36px',
                        fontSize: '0.8125rem',
                        fontFamily: '"Plus Jakarta Sans"',
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                </div>
                <div className="flex-1">
                  <span className="block text-xs font-medium text-surface-500 mb-1.5">Status</span>
                  <Autocomplete
                    options={httpStatusCodes}
                    value={statusCodeFilter}
                    onChange={(_, newValue) => setStatusCodeFilter(newValue === 'all' || newValue === null ? null : newValue)}
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        placeholder="Any"
                        sx={{ '& .MuiOutlinedInput-input': { height: '15px' } }}
                      />
                    )}
                    size="small"
                    fullWidth
                    slotProps={{
                      popper: { style: { width: '140px' }, placement: 'bottom-start' },
                      paper: {
                        sx: {
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          border: '1px solid #E7E5E4',
                          mt: 0.5,
                        },
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        borderRadius: '8px',
                        height: '36px',
                        fontSize: '0.8125rem',
                        fontFamily: '"Plus Jakarta Sans"',
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => setErrorsFilter((prev) => !prev)}
                className="flex items-center justify-between w-full text-left"
              >
                <span className="text-xs font-medium text-surface-500">Errors only (4xx / 5xx)</span>
                <span
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    errorsFilter ? 'bg-error' : 'bg-surface-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-xs transition-transform ${
                      errorsFilter ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        </>
      )}
      {/* Active filter pills */}
      {activeFilterCount > 0 && !showFiltersPanel && (
        <div className="flex items-center gap-2">
          {connectionFilter && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-surface-0 border border-surface-200 rounded-md">
              {`Connection: ${connectionFilter.slice(0, 12)}...`}
              <button type="button" onClick={() => setConnectionFilter(null)} className="text-surface-400 hover:text-surface-600">×</button>
            </span>
          )}
          {integrationFilter && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-surface-0 border border-surface-200 rounded-md">
              {`Integration: ${integrationFilter}`}
              <button type="button" onClick={() => setIntegrationFilter(null)} className="text-surface-400 hover:text-surface-600">×</button>
            </span>
          )}
          {statusCodeFilter && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-surface-0 border border-surface-200 rounded-md">
              {`Status: ${statusCodeFilter}`}
              <button type="button" onClick={() => setStatusCodeFilter(null)} className="text-surface-400 hover:text-surface-600">×</button>
            </span>
          )}
          {requestMethodFilter && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-surface-0 border border-surface-200 rounded-md">
              {`Method: ${requestMethodFilter}`}
              <button type="button" onClick={() => setRequestMethodFilter(null)} className="text-surface-400 hover:text-surface-600">×</button>
            </span>
          )}
          {requestPathFilter && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-surface-0 border border-surface-200 rounded-md">
              {`Path: ${requestPathFilter}`}
              <button type="button" onClick={() => setRequestPathFilter(null)} className="text-surface-400 hover:text-surface-600">×</button>
            </span>
          )}
          {errorsFilter && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-surface-0 border border-surface-200 rounded-md">
              Errors only
              <button type="button" onClick={() => setErrorsFilter(false)} className="text-surface-400 hover:text-surface-600">×</button>
            </span>
          )}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs text-surface-500 hover:text-surface-700 ml-2"
          >
            Clear all
          </button>
        </div>
      )}
      {/* Charts Section */}
      <div className="card">
        <button
          type="button"
          className={`w-full flex items-center justify-center gap-2 py-2 text-surface-400 hover:text-surface-600 transition-colors ${!hideChartWidgets ? 'border-b border-surface-100' : ''}`}
          onClick={() => setHideChartWidgets(!hideChartWidgets)}
        >
          {hideChartWidgets ? <UnfoldMoreIcon sx={{ fontSize: '16px' }} /> : <UnfoldlessIcon sx={{ fontSize: '16px' }} />}
          <span className="text-xs font-medium">{hideChartWidgets ? 'Show Charts' : 'Hide Charts'}</span>
        </button>
        {!hideChartWidgets && (
          <div className="flex divide-x divide-surface-100">
            {/* Status Chart */}
            <div className="p-4 w-1/2 flex flex-col h-56">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-surface-700">API Calls by Status</h3>
                <div className="flex items-center gap-2">
                  {loadingTotalAPICalls ? (
                    <ClipLoader size={14} color="#D4532A" />
                  ) : (
                    <span className="text-lg font-semibold text-surface-900">{totalAPICalls?.toLocaleString()}</span>
                  )}
                  <span className="text-xs text-surface-400">total</span>
                </div>
              </div>
              {loadingLogsByStatus ? (
                <div className="flex-1 flex items-center justify-center">
                  <ClipLoader size={24} color="#D4532A" />
                </div>
              ) : (
                <div className="flex-1 min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={logsByStatus} barCategoryGap={4}>
                      <CartesianGrid
                        vertical={false}
                        stroke={chartGridStyle.stroke}
                        strokeDasharray={chartGridStyle.strokeDasharray}
                      />
                      <XAxis
                        dataKey="request_time"
                        tickFormatter={getTimeBreakdown(endDate, startDate).tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={chartAxisStyle}
                      />
                      <YAxis axisLine={false} tickLine={false} tick={chartAxisStyle} width={45} />
                      <Tooltip content={getBarChartTooltip} wrapperStyle={{ zIndex: 100 }} />
                      <Legend
                        align="left"
                        verticalAlign="bottom"
                        content={renderStatusLegend}
                      />
                      {statusCodes.map((code) => (
                        <Bar
                          key={code}
                          dataKey={code}
                          name={String(code)}
                          stackId="a"
                          fill={statusShadesMap[code] || '#D6D3D1'}
                          radius={[2, 2, 0, 0]}
                          fillOpacity={hoveredStatus && hoveredStatus !== String(code) ? 0.2 : 1}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            {/* Endpoint Chart */}
            <div className="p-4 w-1/2 flex flex-col h-56">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-surface-700">API Calls by Endpoint</h3>
              </div>
              {loadingLogsByEndpoint ? (
                <div className="flex-1 flex items-center justify-center">
                  <ClipLoader size={24} color="#D4532A" />
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto pr-2">
                  {(() => {
                    const sortedEndpoints = [...logsByEndpoint].sort((a, b) => b.count - a.count);
                    const maxCount = sortedEndpoints[0]?.count || 1;
                    const total = sortedEndpoints.reduce((sum, e) => sum + e.count, 0);
                    const barMaxWidth = 50;
                    return (
                      <div className="space-y-1.5">
                        {sortedEndpoints.map((entry) => {
                          const pct = total > 0 ? (entry.count / total) * 100 : 0;
                          const barPx = Math.round((entry.count / maxCount) * barMaxWidth);
                          return (
                            <button
                              type="button"
                              key={`${entry.request_method}-${entry.request_path}`}
                              className="flex items-center gap-2 w-full text-left hover:bg-surface-50
                rounded px-1 -mx-1 transition-colors cursor-pointer"
                              onClick={
                                (e) => {
                                  handleEndpointClick(entry.request_path, entry.request_method, e);
                                }
                              }
                            >
                              <div className={`request-${entry.request_method} w-14 text-center flex-shrink-0`}>
                                {entry.request_method}
                              </div>
                              <span className="text-xs text-surface-600 truncate flex-1 min-w-0">
                                {entry.request_path}
                              </span>
                              <div
                                className="h-2 rounded-sm flex-shrink-0"
                                style={{
                                  width: `${Math.max(barPx, 4)}px`,
                                  backgroundColor: '#A8A29E',
                                }}
                              />
                              <span className="text-xs text-surface-500 tabular-nums w-10 text-right flex-shrink-0">
                                {entry.count.toLocaleString()}
                              </span>
                              <span className="text-[10px] text-surface-400 w-9 text-right flex-shrink-0">{`(${Math.round(pct)}%)`}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Status Filter Popover */}
      {statusFilterPopover.open && (
        <>
          <div
            className="fixed inset-0 z-50"
            onClick={() => {
              setStatusFilterPopover({
                open: false, status: null, x: 0, y: 0,
              });
              setHoveredStatus(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setStatusFilterPopover({
                  open: false, status: null, x: 0, y: 0,
                });
                setHoveredStatus(null);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Close popover"
          />
          <div
            className="fixed z-50 bg-surface-0 rounded-lg border border-surface-200
              shadow-lg py-1 min-w-36"
            style={{
              left: statusFilterPopover.x,
              top: statusFilterPopover.y,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-surface-700
                hover:bg-surface-50 flex items-center gap-2"
              onClick={() => {
                setStatusCodeFilter(String(statusFilterPopover.status));
                setStatusFilterPopover({
                  open: false, status: null, x: 0, y: 0,
                });
                setHoveredStatus(null);
              }}
            >
              <FilterList sx={{ fontSize: '16px' }} />
              <span>
                Filter by
                {' '}
                <span className="font-medium">{statusFilterPopover.status}</span>
              </span>
            </button>
          </div>
        </>
      )}
      {/* Endpoint Filter Popover */}
      {endpointFilterPopover.open && (
        <>
          <div
            className="fixed inset-0 z-50"
            onClick={() => setEndpointFilterPopover({
              open: false, path: null, method: null, x: 0, y: 0,
            })}
            onKeyDown={(e) => e.key === 'Escape' && setEndpointFilterPopover({
              open: false, path: null, method: null, x: 0, y: 0,
            })}
            role="button"
            tabIndex={0}
            aria-label="Close popover"
          />
          <div
            className="fixed z-50 bg-surface-0 rounded-lg border border-surface-200
              shadow-lg py-1 min-w-44"
            style={{
              left: endpointFilterPopover.x,
              top: endpointFilterPopover.y,
            }}
          >
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-surface-700
                hover:bg-surface-50 flex items-center gap-2"
              onClick={() => {
                setRequestPathFilter(endpointFilterPopover.path);
                setEndpointFilterPopover({
                  open: false, path: null, method: null, x: 0, y: 0,
                });
              }}
            >
              <FilterList sx={{ fontSize: '16px' }} />
              <span>Filter by path</span>
            </button>
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-surface-700
                hover:bg-surface-50 flex items-center gap-2"
              onClick={() => {
                setRequestMethodFilter(endpointFilterPopover.method);
                setEndpointFilterPopover({
                  open: false, path: null, method: null, x: 0, y: 0,
                });
              }}
            >
              <FilterList sx={{ fontSize: '16px' }} />
              <span>Filter by HTTP method</span>
            </button>
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-surface-700
                hover:bg-surface-50 flex items-center gap-2"
              onClick={() => {
                setRequestPathFilter(endpointFilterPopover.path);
                setRequestMethodFilter(endpointFilterPopover.method);
                setEndpointFilterPopover({
                  open: false, path: null, method: null, x: 0, y: 0,
                });
              }}
            >
              <FilterList sx={{ fontSize: '16px' }} />
              <span>Filter by both</span>
            </button>
          </div>
        </>
      )}
      {/* Data Grid Section */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden card">
        <DataGridPro
          rows={logRows}
          columns={colHeaders}
          loading={loadingLogRows}
          sortingOrder={['desc', 'asc']}
          sortingMode="server"
          sortModel={sortModel.field ? [sortModel] : []}
          onSortModelChange={(newModel) => {
            const newSortModel = newModel.length > 0 ? newModel[0] : { field: 'request_time', sort: 'desc' };
            setSortModel(newSortModel);
            resetPagination();
            getAPILogRows(null, newSortModel);
          }}
          slots={{
            footer: customFooter,
          }}
          apiRef={gridApiRef}
          disableColumnMenu
          disableColumnSelector
          disableColumnPinning
          rowHeight={36}
          columnHeaderHeight={40}
          sx={{
            border: 'none',
            '--DataGrid-rowBorderColor': '#F5F5F4',
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-scrollbarFiller': {
              backgroundColor: '#FAFAF9 !important',
            },
            '& .MuiDataGrid-filler': {
              backgroundColor: '#FFFFFF !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '1px solid #E7E5E4',
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              fontWeight: 500,
              fontSize: '0.8125rem',
              color: '#78716C',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.8125rem',
              fontWeight: 400,
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              color: '#44403C',
            },
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
              backgroundColor: '#FAFAF9',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: '#F5F5F4',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: '#F5F5F4',
            },
            '& .MuiDataGrid-footerContainer': {
              height: '44px',
              minHeight: '44px',
              maxHeight: '44px',
              borderTop: '1px solid #E7E5E4',
            },
          }}
          onRowClick={(params) => { openApiLogDetail(params.row); }}
          rowSelectionModel={{ type: 'include', ids: new Set(apiLogDetail ? [apiLogDetail.id] : []) }}
          hideFooterSelectedRowCount
        />
      </div>
      <Backdrop
        open={showApiLogDetail}
        onClick={() => {
          setShowApiLogDetail(false);
          removeIdFromUrl();
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1,
          backgroundColor: 'rgba(28, 25, 23, 0.4)',
          backdropFilter: 'blur(4px)',
        }}
      />
      <Drawer
        anchor="right"
        open={showApiLogDetail}
        onClose={() => {
          setShowApiLogDetail(false);
          removeIdFromUrl();
        }}
        PaperProps={{ sx: { width: '50%', boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.08)', borderLeft: 'none' } }}
        variant="persistent"
      >
        <ApiLogDetailDrawer
          showApiLogDetail={showApiLogDetail}
          apiLogDetail={apiLogDetail}
          closeDrawer={() => {
            setShowApiLogDetail(false);
            removeIdFromUrl();
          }}
          getIntegrationDisplayName={
            (integrationType, value) => getDisplayName(integrationType, value)
          }
          requestPathToTitleMap={requestPathToTitleMap}
          setConnectionFilter={setConnectionFilter}
          setIntegrationFilter={setIntegrationFilter}
          setStatusCodeFilter={setStatusCodeFilter}
          setRequestMethodFilter={setRequestMethodFilter}
          setRequestPathFilter={setRequestPathFilter}
        />
      </Drawer>
    </div>
  );
}

export default ApiLogPage;
